// Catalogue data layer (Module 01 §5). TanStack Query hooks wrapping the
// generated api-client (doctrine 3: apps/web talks to the API only through
// @assessly/api-client). Query keys are co-located in `catalogueKeys`; every
// mutation sends a fresh Idempotency-Key (spec 16 §5, §9.15) and invalidates the
// lists it affects. Errors are normalised by `unwrap` into `RequestError`
// (request id + field errors) / `UnauthorizedError` (401 → /login).
//
// Redaction note: cost fields (`*_minor`, `breakdown`, NRV) are omitted by the
// server for roles without catalogue.cost.read, so the typed responses already
// mark them optional — consumers must treat absence as "no access", never as 0.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap, unwrapVoid } from "../../../../lib/request";

// ---- request/response type aliases -----------------------------------------

type ProductsQuery = NonNullable<paths["/v1/catalogue/products"]["get"]["parameters"]["query"]>;
type BomExplodeQuery = NonNullable<paths["/v1/catalogue/boms/{id}"]["get"]["parameters"]["query"]>;
type EffectiveQuery = NonNullable<
  paths["/v1/catalogue/products/{id}/boms/effective"]["get"]["parameters"]["query"]
>;
// Routings are facility-bound (§3.4): the routing-effective endpoint requires
// facility_id, unlike the BOM-effective one.
type EffectiveRoutingQuery = NonNullable<
  paths["/v1/catalogue/products/{id}/routings/effective"]["get"]["parameters"]["query"]
>;
type ProductBomsQuery = NonNullable<
  paths["/v1/catalogue/products/{id}/boms"]["get"]["parameters"]["query"]
>;
type CostItemsQuery = NonNullable<
  paths["/v1/catalogue/cost-runs/{id}/items"]["get"]["parameters"]["query"]
>;
type ChangeLogQuery = NonNullable<paths["/v1/catalogue/change-log"]["get"]["parameters"]["query"]>;
type FloorCacheQuery = NonNullable<
  paths["/v1/catalogue/floor-cache"]["get"]["parameters"]["query"]
>;

type S = components["schemas"];

// ---- query keys -------------------------------------------------------------

/** S1 product-list filters. `attributes` become repeatable `attr.<key>` params. */
export interface ProductFilters extends ProductsQuery {
  attributes?: Record<string, string> | undefined;
}

export const catalogueKeys = {
  all: ["catalogue"] as const,
  products: (filters: ProductFilters = {}) => ["catalogue", "products", filters] as const,
  product: (id: string) => ["catalogue", "product", id] as const,
  whereUsed: (id: string) => ["catalogue", "product", id, "where-used"] as const,
  productConversions: (id: string) => ["catalogue", "product", id, "conversions"] as const,
  productBoms: (id: string, query: ProductBomsQuery = {}) =>
    ["catalogue", "product", id, "boms", query] as const,
  productRoutings: (id: string, facilityId?: string) =>
    ["catalogue", "product", id, "routings", facilityId ?? null] as const,
  effectiveBom: (id: string, query: EffectiveQuery = {}) =>
    ["catalogue", "product", id, "boms", "effective", query] as const,
  effectiveRouting: (id: string, query: EffectiveRoutingQuery) =>
    ["catalogue", "product", id, "routings", "effective", query] as const,
  bom: (id: string, query: BomExplodeQuery = {}) => ["catalogue", "bom", id, query] as const,
  routing: (id: string) => ["catalogue", "routing", id] as const,
  uoms: () => ["catalogue", "uoms"] as const,
  uomConversions: () => ["catalogue", "uom-conversions"] as const,
  attributeSchemas: () => ["catalogue", "attribute-schemas"] as const,
  templates: () => ["catalogue", "templates"] as const,
  costRuns: () => ["catalogue", "cost-runs"] as const,
  costRun: (id: string) => ["catalogue", "cost-run", id] as const,
  costRunItems: (id: string, query: CostItemsQuery = {}) =>
    ["catalogue", "cost-run", id, "items", query] as const,
  changeLog: (query: ChangeLogQuery = {}) => ["catalogue", "change-log", query] as const,
  floorCache: (query: FloorCacheQuery) => ["catalogue", "floor-cache", query] as const,
  facilities: () => ["catalogue", "ref", "facilities"] as const,
} as const;

// Broad prefixes for post-mutation invalidation.
const PRODUCTS = ["catalogue", "products"] as const;
const UOMS = ["catalogue", "uoms"] as const;
const UOM_CONVERSIONS = ["catalogue", "uom-conversions"] as const;
const ATTRIBUTE_SCHEMAS = ["catalogue", "attribute-schemas"] as const;
const TEMPLATES = ["catalogue", "templates"] as const;
const COST_RUNS = ["catalogue", "cost-runs"] as const;

function idempotentHeader() {
  return { "Idempotency-Key": newIdempotencyKey() } as const;
}

function buildProductsQuery(filters: ProductFilters): ProductsQuery {
  const { attributes, ...typed } = filters;
  const query: Record<string, unknown> = { ...typed };
  if (attributes !== undefined) {
    for (const [key, value] of Object.entries(attributes)) query[`attr.${key}`] = value;
  }
  return query;
}

// =============================================================================
// Products
// =============================================================================

export function useProducts(filters: ProductFilters = {}) {
  return useQuery({
    queryKey: catalogueKeys.products(filters),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products", {
          signal,
          params: { query: buildProductsQuery(filters) },
        }),
        "products",
      ),
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: catalogueKeys.product(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}", { signal, params: { path: { id } } }),
        "product",
      ),
  });
}

export function useWhereUsed(id: string) {
  return useQuery({
    queryKey: catalogueKeys.whereUsed(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/where-used", {
          signal,
          params: { path: { id } },
        }),
        "where-used",
      ),
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ProductCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/products", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-product",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUCTS }),
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ProductPatchRequest"]) =>
      unwrap(
        await client.PATCH("/v1/catalogue/products/{id}", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "update-product",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.product(id) }),
        qc.invalidateQueries({ queryKey: PRODUCTS }),
      ]),
  });
}

export function useActivateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/products/{id}/activate", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "activate-product",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.product(id) }),
        qc.invalidateQueries({ queryKey: PRODUCTS }),
      ]),
  });
}

export function useDiscontinueProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ProductDiscontinueRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/products/{id}/discontinue", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "discontinue-product",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.product(id) }),
        qc.invalidateQueries({ queryKey: PRODUCTS }),
      ]),
  });
}

export function useReactivateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/products/{id}/reactivate", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "reactivate-product",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.product(id) }),
        qc.invalidateQueries({ queryKey: PRODUCTS }),
      ]),
  });
}

// =============================================================================
// Product-specific UoM conversions
// =============================================================================

export function useProductConversions(id: string) {
  return useQuery({
    queryKey: catalogueKeys.productConversions(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/conversions", {
          signal,
          params: { path: { id } },
        }),
        "conversions",
      ),
  });
}

export function useCreateProductConversion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ProductConversionCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/products/{id}/conversions", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "create-conversion",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogueKeys.productConversions(id) }),
  });
}

export function useDeleteProductConversion(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversionId: string) => {
      unwrapVoid(
        await client.DELETE("/v1/catalogue/conversions/{id}", {
          params: { path: { id: conversionId }, header: idempotentHeader() },
        }),
        "delete-conversion",
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: catalogueKeys.productConversions(productId) }),
  });
}

// =============================================================================
// UoMs & generic conversions (S5)
// =============================================================================

export function useUoms() {
  return useQuery({
    queryKey: catalogueKeys.uoms(),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/uoms", { signal }), "uoms"),
  });
}

export function useCreateUom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["UomCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/uoms", { params: { header: idempotentHeader() }, body }),
        "create-uom",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: UOMS }),
  });
}

export function useUpdateUom(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["UomPatchRequest"]) =>
      unwrap(
        await client.PATCH("/v1/catalogue/uoms/{id}", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "update-uom",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: UOMS }),
  });
}

export function useArchiveUom(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/uoms/{id}/archive", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "archive-uom",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: UOMS }),
  });
}

export function useUomConversions() {
  return useQuery({
    queryKey: catalogueKeys.uomConversions(),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/uom-conversions", { signal }), "uom-conversions"),
  });
}

export function useCreateUomConversion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["UomConversionCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/uom-conversions", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-uom-conversion",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: UOM_CONVERSIONS }),
  });
}

export function useDeleteUomConversion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      unwrapVoid(
        await client.DELETE("/v1/catalogue/uom-conversions/{id}", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "delete-uom-conversion",
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: UOM_CONVERSIONS }),
  });
}

// =============================================================================
// Attribute schemas
// =============================================================================

export function useAttributeSchemas() {
  return useQuery({
    queryKey: catalogueKeys.attributeSchemas(),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/attribute-schemas", { signal }), "attribute-schemas"),
  });
}

export function useCreateAttributeSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["AttributeSchemaCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/attribute-schemas", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-attribute-schema",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ATTRIBUTE_SCHEMAS }),
  });
}

export function useUpdateAttributeSchema(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["AttributeSchemaPatchRequest"]) =>
      unwrap(
        await client.PATCH("/v1/catalogue/attribute-schemas/{id}", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "update-attribute-schema",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ATTRIBUTE_SCHEMAS }),
  });
}

// =============================================================================
// BOMs (S3)
// =============================================================================

export function useProductBoms(id: string, query: ProductBomsQuery = {}) {
  return useQuery({
    queryKey: catalogueKeys.productBoms(id, query),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/boms", {
          signal,
          params: { path: { id }, query },
        }),
        "product-boms",
      ),
  });
}

export function useBom(id: string, query: BomExplodeQuery = {}) {
  return useQuery({
    queryKey: catalogueKeys.bom(id, query),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/boms/{id}", { signal, params: { path: { id }, query } }),
        "bom",
      ),
  });
}

export function useEffectiveBom(id: string, query: EffectiveQuery = {}) {
  return useQuery({
    queryKey: catalogueKeys.effectiveBom(id, query),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/boms/effective", {
          signal,
          params: { path: { id }, query },
        }),
        "effective-bom",
      ),
  });
}

export function useCreateBomVersion(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["BomCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/products/{id}/boms", {
          params: { path: { id: productId }, header: idempotentHeader() },
          body,
        }),
        "create-bom",
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "boms"] }),
  });
}

export function useUpdateBom(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["BomPatchRequest"]) =>
      unwrap(
        await client.PATCH("/v1/catalogue/boms/{id}", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "update-bom",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogueKeys.bom(id) }),
  });
}

export function useReplaceBomLines(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["BomLinesReplaceRequest"]) =>
      unwrap(
        await client.PUT("/v1/catalogue/boms/{id}/lines", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "replace-bom-lines",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogueKeys.bom(id) }),
  });
}

export function useReplaceBomOutputs(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["BomOutputsReplaceRequest"]) =>
      unwrap(
        await client.PUT("/v1/catalogue/boms/{id}/outputs", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "replace-bom-outputs",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogueKeys.bom(id) }),
  });
}

export function useActivateBom(id: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["VersionActivateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/boms/{id}/activate", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "activate-bom",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.bom(id) }),
        qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "boms"] }),
      ]),
  });
}

/**
 * Withdraw a SCHEDULED activation (spec 01 §3.3.5, D-261): `active → archived`
 * while `effective_from > tenant-local today`, restoring the predecessor the
 * change log recorded. The response's `restored_version_id` is NULL when the
 * withdrawn version superseded nothing — the product is then left with no
 * active version, which the screen states as a fact, not an error.
 *
 * Invalidation covers the version, the product's version list AND the §3.4
 * resolver (`…/boms/effective` shares the `["catalogue","product",id,"boms"]`
 * prefix), so "effective today" re-answers the moment the row changes. A
 * REFUSAL invalidates too: VERSION_IN_FORCE / VERSION_NOT_ACTIVE /
 * PREDECESSOR_NOT_RESTORABLE each mean the client's picture was stale, and the
 * dialog's sentence for VERSION_NOT_ACTIVE promises a refreshed screen.
 */
export function useWithdrawBom(id: string, productId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: catalogueKeys.bom(id) }),
      qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "boms"] }),
    ]);
  return useMutation({
    mutationFn: async (body: S["VersionWithdrawRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/boms/{id}/withdraw", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "withdraw-bom",
      ),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

/**
 * Archive a version (spec 01 §3.3.5): `draft → archived` abandons,
 * `superseded → archived` housekeeps. A REFUSAL invalidates too, as withdraw's
 * does: `*_NOT_ARCHIVABLE` means the row's status moved under the client
 * (a concurrent withdraw restored it, or it was activated), and the dialog's
 * sentence for it promises a refreshed screen; `VERSION_STILL_RESUMABLE`
 * (D-261 amended) re-reads the schedule the planner is told to withdraw.
 */
export function useArchiveBom(id: string, productId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: catalogueKeys.bom(id) }),
      qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "boms"] }),
    ]);
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/boms/{id}/archive", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "archive-bom",
      ),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

// =============================================================================
// Routings (S4)
// =============================================================================

export function useProductRoutings(id: string, facilityId?: string) {
  return useQuery({
    queryKey: catalogueKeys.productRoutings(id, facilityId),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/routings", {
          signal,
          params: {
            path: { id },
            query: facilityId !== undefined ? { facility_id: facilityId } : {},
          },
        }),
        "product-routings",
      ),
  });
}

export function useRouting(id: string) {
  return useQuery({
    queryKey: catalogueKeys.routing(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/routings/{id}", { signal, params: { path: { id } } }),
        "routing",
      ),
  });
}

export function useEffectiveRouting(id: string, query: EffectiveRoutingQuery) {
  return useQuery({
    queryKey: catalogueKeys.effectiveRouting(id, query),
    retry: false,
    enabled: id.length > 0 && query.facility_id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/routings/effective", {
          signal,
          params: { path: { id }, query },
        }),
        "effective-routing",
      ),
  });
}

export function useCreateRoutingVersion(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["RoutingCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/products/{id}/routings", {
          params: { path: { id: productId }, header: idempotentHeader() },
          body,
        }),
        "create-routing",
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "routings"] }),
  });
}

export function useUpdateRouting(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["RoutingPatchRequest"]) =>
      unwrap(
        await client.PATCH("/v1/catalogue/routings/{id}", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "update-routing",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogueKeys.routing(id) }),
  });
}

export function useReplaceRoutingSteps(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["RoutingStepsReplaceRequest"]) =>
      unwrap(
        await client.PUT("/v1/catalogue/routings/{id}/steps", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "replace-routing-steps",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogueKeys.routing(id) }),
  });
}

export function useActivateRouting(id: string, productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["VersionActivateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/routings/{id}/activate", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "activate-routing",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.routing(id) }),
        qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "routings"] }),
      ]),
  });
}

/** Withdraw a SCHEDULED routing activation — same rule, refusals and response
 * shape as `useWithdrawBom` (spec 01 §3.3.5, D-261). */
export function useWithdrawRouting(id: string, productId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: catalogueKeys.routing(id) }),
      qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "routings"] }),
    ]);
  return useMutation({
    mutationFn: async (body: S["VersionWithdrawRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/routings/{id}/withdraw", {
          params: { path: { id }, header: idempotentHeader() },
          body,
        }),
        "withdraw-routing",
      ),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

/** Archive a routing version — same rule, refusals and invalidation (on a
 * refusal too) as `useArchiveBom` (spec 01 §3.3.5). */
export function useArchiveRouting(id: string, productId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: catalogueKeys.routing(id) }),
      qc.invalidateQueries({ queryKey: ["catalogue", "product", productId, "routings"] }),
    ]);
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/routings/{id}/archive", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "archive-routing",
      ),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

export function usePutRoutingStepBindings() {
  return useMutation({
    mutationFn: async (body: S["RoutingStepBindingsPutRequest"]) =>
      unwrap(
        await client.PUT("/v1/catalogue/routing-step-bindings", {
          params: { header: idempotentHeader() },
          body,
        }),
        "routing-step-bindings",
      ),
  });
}

// =============================================================================
// Cost runs (S7) — every field here is cost-gated; see redaction note above.
// =============================================================================

/** Active facilities — reference data (barely changes within a session; one
 * fetch, cached), read here for the FACILITY-calendar default of a new run's
 * `as_of_date` (D-089: data selection is facility-local). Same per-area shape
 * as production's and inventory's `useFacilities` — areas own their hooks. */
export function useFacilities() {
  return useQuery({
    queryKey: catalogueKeys.facilities(),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities", { signal, params: { query: { status: "active" } } }),
        "facilities",
      ),
  });
}

export function useCostRuns() {
  return useQuery({
    queryKey: catalogueKeys.costRuns(),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/cost-runs", { signal }), "cost-runs"),
  });
}

export function useCostRun(id: string) {
  return useQuery({
    queryKey: catalogueKeys.costRun(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/cost-runs/{id}", { signal, params: { path: { id } } }),
        "cost-run",
      ),
  });
}

export function useCostRunItems(id: string, query: CostItemsQuery = {}) {
  return useQuery({
    queryKey: catalogueKeys.costRunItems(id, query),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/cost-runs/{id}/items", {
          signal,
          params: { path: { id }, query },
        }),
        "cost-run-items",
      ),
  });
}

export function useCreateCostRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["CostRunCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/cost-runs", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-cost-run",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: COST_RUNS }),
  });
}

export function useReleaseCostRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/cost-runs/{id}/release", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "release-cost-run",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.costRun(id) }),
        qc.invalidateQueries({ queryKey: COST_RUNS }),
        qc.invalidateQueries({ queryKey: PRODUCTS }),
      ]),
  });
}

export function useDiscardCostRun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/cost-runs/{id}/discard", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "discard-cost-run",
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: catalogueKeys.costRun(id) }),
        qc.invalidateQueries({ queryKey: COST_RUNS }),
      ]),
  });
}

// =============================================================================
// Templates (S8)
// =============================================================================

export function useTemplates() {
  return useQuery({
    queryKey: catalogueKeys.templates(),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/templates", { signal }), "templates"),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["TemplateCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/catalogue/templates", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-template",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES }),
  });
}

export function useArchiveTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await client.POST("/v1/catalogue/templates/{id}/archive", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "archive-template",
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES }),
  });
}

// =============================================================================
// Change log & floor cache
// =============================================================================

export function useChangeLog(query: ChangeLogQuery = {}) {
  return useQuery({
    queryKey: catalogueKeys.changeLog(query),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/change-log", { signal, params: { query } }),
        "change-log",
      ),
  });
}

export function useFloorCache(query: FloorCacheQuery) {
  return useQuery({
    queryKey: catalogueKeys.floorCache(query),
    retry: false,
    enabled: query.facility_id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/floor-cache", { signal, params: { query } }),
        "floor-cache",
      ),
  });
}
